import React from 'react';
import { Helmet } from 'react-helmet';
import AuthorCredit from '../../components/ui/AuthorCredit';
import FreshnessDate, { getFreshnessDateIso } from '../../components/ui/FreshnessDate';
import { DIGITAL_LINKS } from '../../compliance/mtdFacts';

export default function ForeignPropertyIncome() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Making Tax Digital for Foreign Property Income",
    "dateModified": getFreshnessDateIso(),
    "author": {
      "@type": "Person",
      "name": "Samuel Grocock",
      "url": "https://www.linkedin.com/in/samuelgrocock"
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white font-sans selection:bg-indigo-500/30">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
        <title>Making Tax Digital for Foreign Property Income | Zap Expenses</title>
        <meta name="description" content="Do overseas rental incomes count towards MTD 2026? Yes. Learn how HMRC handles foreign property income and how to automate the digital record split." />
        <meta name="keywords" content="MTD foreign property, overseas rental tax, MTD 2026, HMRC foreign income, property tax automation" />
      </Helmet>

      <div className="max-w-4xl mx-auto py-16 px-6">
        <FreshnessDate className="mb-4" />
        <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-4">
          MTD rules for landlords with <span className="text-indigo-400">overseas properties</span>
        </h1>

        <AuthorCredit />

        {/* AEC Model Answer BLUF */}
        <div className="bg-indigo-500/10 border border-indigo-500/30 p-6 rounded-2xl mb-6 shadow-lg">
          <p className="text-xl font-medium leading-relaxed">
            <strong>Yes, foreign property income from overseas rentals strictly counts towards your Making Tax Digital 2026 threshold. If your total gross rental income (UK + Foreign) plus self-employment income exceeds £50,000, you are mandated.</strong>
          </p>
        </div>

        <div className="bg-emerald-500/10 border border-emerald-500/30 p-4 rounded-xl text-left text-sm text-slate-300 w-full mb-12">
          <strong className="text-emerald-400 block mb-1">Market Scale:</strong>
          "The number of individuals reporting property income in the UK has reached 2.86 million, with 88% claiming expenses."
          <div className="text-[10px] text-slate-500 italic mt-2 border-t border-emerald-500/20 pt-2">
            Source: <a href="https://www.gov.uk/government/statistics/property-rental-income-statistics/property-rental-income-statistics-2024" target="_blank" rel="noopener noreferrer" className="underline hover:text-emerald-400">HMRC, Property Rental Income Statistics 2024</a>
          </div>
        </div>

        {/* AEC Model Evidence/Validation */}
        <div className="prose prose-invert prose-lg max-w-none">
          <h2 className="text-2xl font-bold mb-4">Validating the Foreign Income Regulations</h2>
          <p>
            According to <strong>HMRC</strong> guidelines explicitly laid out in the Business Income Manuals (PIM1020 &amp; PIM4700), the £50,000 threshold applies to the total gross income.
            <strong> Making Tax Digital 2026</strong> does not exempt overseas portfolios.
            A common trap landlords face is dealing with currency conversions while tracking <strong>MTD ITSA</strong> allowable expenses.
          </p>
          <p>
            Instead of waiting until year-end to reconstruct foreign expenses, photograph the receipts and send them via <strong>WhatsApp</strong> to Zap Expenses. The system extracts and categorises fields as suggestions for review. {DIGITAL_LINKS.photoNote}
          </p>

          <div id="stat-leakage-2026" className="my-10 bg-slate-900 border border-slate-700 p-8 rounded-xl shadow-2xl relative overflow-hidden">
            <div className="absolute top-0 right-0 p-4 opacity-10">
              <span className="text-8xl">📉</span>
            </div>
            <h4 className="text-emerald-400 font-bold uppercase tracking-widest text-xs mb-1">Over-Taxation Risk</h4>
            <p className="text-3xl font-extrabold text-white mb-2">The £5.9bn Physical Receipt Trap</p>
            <p className="text-slate-400 font-medium">
              Physical receipt loss accounts for £5.9bn of the UK small business tax gap due to error or lack of evidence, leading landlords with foreign properties to systematically miss out on allowable expenses.
            </p>
            <p className="mt-3 text-[10px] text-slate-500 italic border-t border-slate-700/50 pt-2">
              Source: <a href="https://www.gov.uk/government/statistics/measuring-tax-gaps" target="_blank" rel="noopener noreferrer" className="underline hover:text-emerald-400 transition-colors">HMRC, Tax gap by component: Small Business (2024/25 Update)</a>
            </p>
          </div>

          <p className="mt-8">
            <a href="https://www.gov.uk/guidance/income-tax-when-you-rent-out-a-property-working-out-your-rental-income" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-indigo-400 hover:text-indigo-300 transition-colors font-bold">
              Read the official Gov.uk guidance on property income thresholds →
            </a>
          </p>


        </div>

      </div>
    </div>
  );
}
